import React, { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import styled from 'styled-components'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import { themeOptions } from '@/v2/themes/Silver'
import { ModalProvider as RoutesProvider } from '@/v2/contexts/RoutesContext'
import { SIDEBAR_WIDTH } from '@/v2/constants'
import useDetectDevice from '@/v2/hooks/useDetectDevice'
import Sidebar from '@/v2/containers/sidebar'
import SettingsSidebar from '@/v2/containers/settings/SettingsSidebar'
import { useUser } from '@/v2/hooks/useUser'
import { useAuthContext } from '@/v2/contexts/AuthContext'
import Topbar from './Topbar'
import TopbarMobile from './Topbar/mobile'
import BottomBar from './BottomBar'

const Wrapper = styled.div`
  display: flex;
  overflow-y: auto;
  position: relative;
  width: 100%;
  min-height: 100vh;
  min-height: 100dvh;

  *,
  *:after,
  *:before {
    font-family: ${({ fontFamily }) => fontFamily};
    letter-spacing: ${({ letterSpacing }) => letterSpacing};
  }

  b,
  strong {
    font-weight: 500;
  }
`

const Content = styled.div`
  width: ${({ $isMobile, $hideSidebar }) =>
    $isMobile || $hideSidebar ? '100%' : `calc(100% - ${SIDEBAR_WIDTH})`};
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 1366px) {
    width: ${({ $isMobile, $hideSidebar }) =>
      $isMobile || $hideSidebar ? '100%' : 'calc(100% - 300px)'};
  }
`

const authRoutes = ['login', 'new-password', 'reset-password', 'insurance-form', 'health-history-form']

// Routes where sidebar should be hidden
const noSidebarRoutes = [
  ...authRoutes,
  'dashboard', // Hide sidebar for performance dashboard
]

const ThemeContainer = () => {
  const location = useLocation()
  const [hideSidebar, setHideSidebar] = useState(true)
  const [isAuthRoute, setIsAuthRoute] = useState(true)
  const theme = createTheme(themeOptions)
  const { isMobile } = useDetectDevice()
  const { isLoggedIn } = useAuthContext()
  const { isDashboardOnly } = useUser({ enabled: isLoggedIn })
  const showSidebar = !isMobile && !hideSidebar
  const isSettingsScreen = location.pathname.includes('/settings')

  useEffect(() => {
    const isAuth = authRoutes.some(route => location.pathname.includes(route))
    setIsAuthRoute(isAuth)
    setHideSidebar(
      noSidebarRoutes.some(route => location.pathname.includes(route))
    )
  }, [location.pathname])

  const renderSidebar = () => {
    if (isSettingsScreen) {
      return <SettingsSidebar />
    }
    return <Sidebar />
  }

  return (
    <ThemeProvider theme={theme}>
      <RoutesProvider>
        {page => (
          <Wrapper
            fontFamily={theme.typography.fontFamily}
            letterSpacing={theme.typography.letterSpacing || '0'}
          >
            {showSidebar && renderSidebar()}
            <Content $isMobile={isMobile} $hideSidebar={hideSidebar}>
              {!isAuthRoute &&
                !isDashboardOnly &&
                (!isMobile ? <Topbar /> : <TopbarMobile />)}
              {page}
              {!isAuthRoute && !isDashboardOnly && isMobile && <BottomBar />}
            </Content>
          </Wrapper>
        )}
      </RoutesProvider>
    </ThemeProvider>
  )
}

export default ThemeContainer
